import React, { useMemo } from 'react';
import Popup from '../../../components/general/Popup.tsx';
import { FixedAssetPreviewPopup } from '../../../components/specific/FixedAssetPreview';
import { useFixedAssetDetails } from '../../../hooks/useFixedAssetDetails.ts';
import type { GlAccount } from '../../../hooks/useGLAccounts.ts';
import CreateFixedAssetModal from '../../../pages/app/partner/pages/partnerDashboardPage/components/FixedAssets/CreateFixedAssetModal.tsx';
import { FixedAssetChoicePopup } from './FixedAssetChoicePopup.tsx';
import { LinkExistingFixedAssetPopup } from './LinkExistingFixedAssetPopup.tsx';
import { useBillFixedAsset } from '../hooks/useBillFixedAsset.ts';
import type { TFICDocument } from '../types/Document.ts';
import { prefillFixedAssetFromBill } from '../utils/fixedAssetPrefill.ts';

export type FixedAssetPopup = 'choose' | 'create' | 'link' | 'unlink' | 'preview' | null;

type Props = {
    document: TFICDocument;
    businessId: string;
    documentId: string;
    glAccounts: GlAccount[];
    glAccountsLoading: boolean;
    openPopup: FixedAssetPopup;
    onOpenPopup: (popup: FixedAssetPopup) => void;
};

export function BillFixedAssetDialogs({
    document,
    businessId,
    documentId,
    glAccounts,
    glAccountsLoading,
    openPopup,
    onOpenPopup,
}: Props) {
    const bill = document.editable_bill ?? null;
    const linkedFixedAsset = document.fixed_asset;

    const fixedAsset = useBillFixedAsset({
        businessId,
        documentId,
        billVersion: bill?.version ?? null,
        enabled: !linkedFixedAsset,
    });
    const details = useFixedAssetDetails(
        openPopup === 'preview' ? businessId : null,
        linkedFixedAsset?.id ?? null,
    );
    const initialValues = useMemo(() => (bill ? prefillFixedAssetFromBill(bill) : undefined), [bill]);

    if (!bill) {
        return null;
    }

    const close = () => onOpenPopup(null);

    return (
        <>
            <FixedAssetChoicePopup
                open={openPopup === 'choose'}
                linkableCount={fixedAsset.linkableFixedAssets.length}
                isLoadingLinkable={fixedAsset.isLoadingFixedAssets}
                onClose={close}
                onChoose={onOpenPopup}
            />
            <CreateFixedAssetModal
                open={openPopup === 'create'}
                onClose={close}
                onSubmit={fixedAsset.createFromBill}
                glAccountOptions={glAccounts}
                glAccountsLoading={glAccountsLoading}
                initialValues={initialValues}
            />
            <LinkExistingFixedAssetPopup
                open={openPopup === 'link'}
                fixedAssets={fixedAsset.linkableFixedAssets}
                isBusy={fixedAsset.isBusy}
                onClose={close}
                onConfirm={(fixedAssetId) => {
                    void fixedAsset.linkExisting(fixedAssetId).then(close, () => undefined);
                }}
            />
            <Popup
                open={openPopup === 'unlink'}
                onClose={close}
                onConfirm={() => void fixedAsset.unlink().then(close, () => undefined)}
                title="Vast actief ontkoppelen"
                confirmButtonDisplayLabel="Ontkoppelen"
                confirmButtonDisabled={fixedAsset.isBusy}
                Icon={null}
            >
                <p className="py-2 text-sm text-gray-600">
                    {linkedFixedAsset?.name} wordt losgekoppeld van deze factuur. De regels blijven op de
                    activarekening geboekt; boek ze zelf om als dat nodig is.
                </p>
            </Popup>
            <FixedAssetPreviewPopup
                open={openPopup === 'preview'}
                onClose={close}
                details={details.data}
                isLoading={details.isLoading}
                isError={details.isError}
            />
        </>
    );
}
